import { isEmpty, pickBy } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Actions } from 'react-use/lib/useSet';
import { mapToSelectedSectionSettings } from 'src/utils/types/selectedSectionSettingsWithOptionals';
import {
  Listing,
  SectionInfo,
  SelectedSectionSettings,
} from 'src/WebApiController';

export const usePricingSectionScoreRecommendations = (
  compListingSelectedSectionSettings: SelectedSectionSettings | null,
  onUpdateCompListingSelectedSectionSettings: (
    settings: SelectedSectionSettings | null
  ) => void,
  recommendedSelectedSectionSettings:
    | SelectedSectionSettings
    | null
    | undefined,
  sections: SectionInfo[],
  selectedListing: Listing | undefined,
  listingRecommendationDeclinedMutation: Actions<number> | undefined,
  disabled: boolean
) => {
  const [recommendingSectionIds, setRecommendingSectionIds] = useState<
    number[]
  >([]);

  useEffect(() => {
    setRecommendingSectionIds(
      recommendedSelectedSectionSettings?.sectionIdFilter ?? []
    );
  }, [recommendedSelectedSectionSettings]);

  const onDeclineRecommendation = useCallback(() => {
    if (disabled) return;
    // Exclude the recommending sectionIds
    const updatedSectionIds = (
      compListingSelectedSectionSettings?.sectionIdFilter ?? []
    ).filter((id) => !recommendingSectionIds?.includes(id));

    const selectedSections = sections.filter(({ id }) =>
      updatedSectionIds?.includes(id)
    );
    const sectionIdFilter = selectedSections.map((s) => s.id);
    const selectedRowIds = selectedSections
      .flatMap(({ rows }) => rows)
      .map(({ id }) => id);
    const rowIdFilter = [];
    if (isEmpty(compListingSelectedSectionSettings?.rowIdFilter)) {
      rowIdFilter.push(...selectedRowIds);
    } else {
      rowIdFilter.push(
        ...(compListingSelectedSectionSettings?.rowIdFilter?.filter((rowId) =>
          selectedRowIds.includes(rowId)
        ) ?? [])
      );
    }
    const sectionRowIdFilter = pickBy(
      Object.assign(
        {},
        compListingSelectedSectionSettings?.sectionRowIdFilter ?? {}
      ),
      (_, key) => sectionIdFilter.includes(parseInt(key))
    );
    const compListingSelectedSectionSettingsNew = {
      ...compListingSelectedSectionSettings,
      sectionIdFilter,
      rowIdFilter,
      sectionRowIdFilter,
    };

    onUpdateCompListingSelectedSectionSettings(
      mapToSelectedSectionSettings(compListingSelectedSectionSettingsNew) ??
        null
    );
    selectedListing?.id &&
      listingRecommendationDeclinedMutation &&
      listingRecommendationDeclinedMutation.add(selectedListing?.id);
  }, [
    compListingSelectedSectionSettings,
    disabled,
    listingRecommendationDeclinedMutation,
    onUpdateCompListingSelectedSectionSettings,
    recommendingSectionIds,
    sections,
    selectedListing?.id,
  ]);

  const onToggleRecommendingSectionIds = useCallback(
    (
      sectionIds: number[],
      option?: { include?: boolean; exclude?: boolean }
    ) => {
      if (disabled) {
        return;
      }
      const sanitizedIds = sectionIds.filter((id) =>
        recommendedSelectedSectionSettings?.sectionIdFilter?.includes(id)
      );
      setRecommendingSectionIds((prev) => {
        const updated = Array.from(new Set([...prev, ...sanitizedIds]));
        if (option?.include) {
          return updated;
        }
        if (option?.exclude) {
          return prev.filter((id) => !sanitizedIds.includes(id));
        }
        for (const id of prev) {
          if (sanitizedIds.includes(id)) {
            updated.splice(updated.indexOf(id), 1);
          }
        }
        return updated;
      });
    },
    [disabled, recommendedSelectedSectionSettings?.sectionIdFilter]
  );

  const sanitizedRecomendingSectionId = useMemo(() => {
    if (disabled) return;
    return recommendingSectionIds;
  }, [disabled, recommendingSectionIds]);

  return {
    recommendingSectionIds: sanitizedRecomendingSectionId,
    onDeclineRecommendation,
    onToggleRecommendingSectionIds,
  };
};
